/** Вкладки экрана позиций объёма, «скрыть повторы вне суммы» и счётчики
 *  (выпуск 1б плана по замечаниям ПТО, №4 п. 8; выпуск 2 — счётчик и список
 *  «есть в ВОР, нет в СП»).
 *
 *  Вкладка — по части РД строки: «Материалы (СП01/СО)», «Объёмы работ (ВР01)»,
 *  «Прочее» (листы чертежей со своими таблицами, кабельные журналы, опросные
 *  листы…). Признаки «в сумме / повтор / сверка» — с бэкенда, из того же прохода,
 *  что и ведомость материалов (`GET …/volume-selection`); экран их не вычисляет.
 *  Последней — «Анализ комплектности» (№7, выпуск 6): не часть РД, а находки
 *  анализа комплекта; есть только при включённом флаге `OSNOVA_PTO_RD_GAPS`.
 *
 *  Замечание ПТО 23.10, пп. 9 и 15 («АР2 — все материалы ушли в Прочее»):
 *  металлопрокат и сводные таблицы — «Материалы» на любом листе; работы по
 *  чертежам (отверстия и проёмы, работы ИИ, площадь окраски — признак
 *  бэкенда `drawing_work`) — «Объёмы работ»; в ревизии без частей СП/СО (АР1,
 *  АР2, КМ2) в «Материалы» идут спецификации листов чертежей (кроме показателей
 *  объекта, `not_material`) и позиции ИИ из перечня материалов.
 *
 *  Без импортов значений (только `import type`): модуль проверяет `node --test`
 *  (`volumes/tabs.test.mjs`), а Node не знает путей-алиасов и расширений `.ts`. */
import type { PtoVolumeMark, PtoVolumePosition } from '@shared/api';

export type VolumeTab = 'materials' | 'works' | 'other';

/** Вкладки слева направо. */
export const VOLUME_TABS: readonly { id: VolumeTab; title: string }[] = [
  { id: 'materials', title: 'Материалы (СП01/СО)' },
  { id: 'works', title: 'Объёмы работ (ВР01)' },
  { id: 'other', title: 'Прочее' },
];

/** Причины бэкенда (признаки отбора, замечание ПТО 23.10, п. 9): показатель
 *  объекта (ТЭП) — не материал; работа по чертежу — «Объёмы работ». */
export const NOT_MATERIAL = 'not_material';
export const DRAWING_WORK = 'drawing_work';

/** Признаки ревизии для вкладок (замечание ПТО 23.10, п. 15). */
export interface TabContext {
  /** В ревизии нет частей СП/СО (АР1, АР2, КМ2): «Материалы» — спецификации
   *  листов чертежей и позиции ИИ из перечня материалов. */
  sheetSpecs: boolean;
  /** Есть работы по чертежам (признак `drawing_work`). */
  drawingWorks: boolean;
}

/** Без признаков ревизии — прежние вкладки по части РД. */
export const NO_TAB_CONTEXT: TabContext = { sheetSpecs: false, drawingWorks: false };

/** Подпись вкладки: в ревизии без СП/СО — «Материалы (спецификации на
 *  листах)», с работами по чертежам — «Объёмы работ (ВР01 и по чертежам)». */
export function tabTitle(tab: VolumeTab, ctx: TabContext = NO_TAB_CONTEXT): string {
  if (tab === 'materials' && ctx.sheetSpecs) return 'Материалы (спецификации на листах)';
  if (tab === 'works' && ctx.drawingWorks) return 'Объёмы работ (ВР01 и по чертежам)';
  return VOLUME_TABS.find((t) => t.id === tab)?.title ?? '';
}

/** Вкладки ревизии слева направо — с подписями [`tabTitle`]. */
export function volumeTabs(ctx: TabContext = NO_TAB_CONTEXT): { id: VolumeTab; title: string }[] {
  return VOLUME_TABS.map((t) => ({ id: t.id, title: tabTitle(t.id, ctx) }));
}

/** Вкладка «Анализ комплектности» (№7): чего, возможно, не хватает в комплекте —
 *  по нормам ГЭСН, типовому комплекту, сверке ВОР↔СП и ИИ по кнопке. Позиций
 *  не фильтрует; видна, только когда анализ включён для проекта и роли. */
export const GAPS_TAB = 'gaps';
export const GAPS_TAB_TITLE = 'Анализ комплектности';

/** Вкладка экрана: часть РД или «Анализ комплектности». */
export type ScreenTab = VolumeTab | typeof GAPS_TAB;

/** Открытая вкладка: выбранная, если она есть на экране; иначе — по умолчанию
 *  (`defaultTab`). «Анализ комплектности» без включённого анализа (флаг
 *  выключили, роль ниже нужной) не открывается. */
export function activeScreenTab(
  pick: ScreenTab | null,
  stats: Record<VolumeTab, TabStat>,
  gapsOn: boolean,
): ScreenTab {
  if (pick === GAPS_TAB) return gapsOn ? GAPS_TAB : defaultTab(stats);
  return pick ?? defaultTab(stats);
}

/** Часть РД открытой вкладки; на «Анализе комплектности» — null (фильтров
 *  позиций и таблицы нет). */
export function partTabOf(tab: ScreenTab): VolumeTab | null {
  return tab === GAPS_TAB ? null : tab;
}

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}

/** Латиница-двойник в коде части («CП01», «BP01») — кириллицей. */
const HOMOGLYPHS: Record<string, string> = {
  A: 'А',
  B: 'В',
  C: 'С',
  E: 'Е',
  H: 'Н',
  K: 'К',
  M: 'М',
  O: 'О',
  P: 'Р',
  T: 'Т',
  X: 'Х',
  Y: 'У',
};

/** Буквы кода части РД: «СП01» → «СП», «ЧТЖ05» → «ЧТЖ»; нет букв — null. */
export function partLetters(code: string | null | undefined): string | null {
  if (typeof code !== 'string') return null;
  const folded = code
    .trim()
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => HOMOGLYPHS[c] ?? c)
    .replace(/Ё/g, 'Е');
  const m = /^[А-Я]+/.exec(folded);
  return m ? m[0] : null;
}

/** Вкладка по коду части РД: СП и СО — материалы, ВР и ВОР — работы, прочие
 *  коды (ЧТЖ, КТЖ, ОЛ, ОД…) — «Прочее»; кода нет — null. */
export function tabOfPart(code: string | null | undefined): VolumeTab | null {
  const letters = partLetters(code);
  if (!letters) return null;
  if (letters === 'СП' || letters === 'СО') return 'materials';
  if (letters === 'ВР' || letters === 'ВОР') return 'works';
  return 'other';
}

/** Поля позиции, по которым выбирается вкладка. */
export type TabFields = Pick<PtoVolumePosition, 'source_role' | 'raw'> & {
  doc_part?: string | null;
};

/**
 * Вкладка позиции по части РД (правило выпуска 1б; основа [`volumeTab`] и
 * счётчиков ведомости):
 * 1. часть РД строки (`doc_part`); у позиции ИИ («ИИ») и позиции по документу
 *    изменения («ДИ») это не часть листа — для ИИ смотрим лист, позиции
 *    документа изменения — материалы;
 * 2. код части с основной надписи листа (`raw.sheet.code`);
 * 3. без них — по виду таблицы, как запасная часть РД: сводная
 *    (`raw.summary_table`) — материалы, ВОР (`raw.table_kind = vor`) — работы,
 *    прочие таблицы и позиции ИИ без листа — «Прочее».
 */
export function partTab(p: TabFields): VolumeTab {
  const part = typeof p.doc_part === 'string' ? p.doc_part.trim() : '';
  if (part === 'ДИ') return 'materials';
  if (part && part !== 'ИИ') {
    const byPart = tabOfPart(part);
    if (byPart) return byPart;
  }
  const raw = asObj(p.raw);
  const sheet = asObj(raw?.sheet);
  const bySheet = tabOfPart(typeof sheet?.code === 'string' ? sheet.code : null);
  if (bySheet) return bySheet;
  if (raw?.summary_table === true) return 'materials';
  if (raw?.table_kind === 'vor') return 'works';
  return 'other';
}

/** Позиция в перечне материалов по признаку бэкенда: в сумме, «на 1 …» или
 *  без количества (в ведомости отдельной строкой вне итога) — не повтор и не
 *  работа. */
function inList(m: PtoVolumeMark | undefined): boolean {
  if (!m || m.repeat) return false;
  const status = m.status ?? (m.in_sum ? 'sum' : 'excluded');
  return (
    status === 'sum' ||
    (status === 'excluded' && (m.reason === 'per_unit' || m.reason === 'not_quantity'))
  );
}

/**
 * Вкладка позиции (замечание ПТО 23.10, пп. 9 и 15):
 * 1. позиция документа изменения («ДИ») — материалы;
 * 2. работа по чертежу (признак `drawing_work`: отверстия и проёмы, работы
 *    ИИ листов чертежей, площадь окраски) — «Объёмы работ»;
 * 3. строки металлопроката (`raw.table_kind = steel`) и сводных таблиц
 *    (`raw.summary_table`) — материалы на любом листе: раньше, чем код части
 *    «ЧТЖ…» отправит их в «Прочее» (КМ2 ЧТЖ01, ОД01);
 * 4. часть РД, код листа, вид таблицы — [`partTab`];
 * 5. в ревизии без частей СП/СО (`ctx.sheetSpecs`) из «Прочего» — в
 *    «Материалы»: строки таблиц-спецификаций (кроме показателей объекта,
 *    `not_material`) и позиции ИИ из перечня материалов.
 * Признака нет (не загружены) — правила 2 и 5 для позиций ИИ не действуют.
 */
export function volumeTab(
  p: TabFields,
  mark?: PtoVolumeMark,
  ctx: TabContext = NO_TAB_CONTEXT,
): VolumeTab {
  const part = typeof p.doc_part === 'string' ? p.doc_part.trim() : '';
  if (part === 'ДИ') return 'materials';
  if (mark?.reason === DRAWING_WORK) return 'works';
  const raw = asObj(p.raw);
  if (isTableRow(p) && (raw?.table_kind === 'steel' || raw?.summary_table === true)) {
    return 'materials';
  }
  const byPart = partTab(p);
  if (byPart !== 'other' || !ctx.sheetSpecs) return byPart;
  if (isTableRow(p)) {
    return raw?.table_kind === 'spec' && mark?.reason !== NOT_MATERIAL ? 'materials' : 'other';
  }
  return p.source_role === 'rd' && inList(mark) ? 'materials' : 'other';
}

/** Признаки ревизии для вкладок: нет ни одной строки части СП/СО (позиции
 *  документа изменения не в счёт); есть работы по чертежам. */
export function tabContext(
  items: readonly TabFields[],
  marks: ReadonlyMap<string, PtoVolumeMark>,
): TabContext {
  const own = items.filter((p) => (typeof p.doc_part === 'string' ? p.doc_part.trim() : '') !== 'ДИ');
  return {
    sheetSpecs: own.length > 0 && !own.some((p) => partTab(p) === 'materials'),
    drawingWorks: [...marks.values()].some((m) => m.reason === DRAWING_WORK),
  };
}

/** Признаки отбора по id позиции. */
export function marksById(items: readonly PtoVolumeMark[] | undefined): Map<string, PtoVolumeMark> {
  return new Map((items ?? []).map((m) => [m.id, m]));
}

/** Повтор вне суммы по признаку бэкенда: число учтено в другой строке ревизии. */
export function isRepeat(id: string, marks: ReadonlyMap<string, PtoVolumeMark>): boolean {
  return marks.get(id)?.repeat === true;
}

/** Поля позиции для фильтров экрана. */
export type FilterFields = TabFields & Pick<PtoVolumePosition, 'id' | 'needs_review'>;

export interface VolumeFilter {
  tab: VolumeTab;
  /** «Скрыть повторы вне суммы» (по умолчанию включено). */
  hideRepeats: boolean;
  /** «Только на проверку». */
  onlyReview: boolean;
  /** Список «Есть в ВОР, нет в СП» (выпуск 2): строки ВОР, похожие на
   *  материалы, без пары в спецификации — со всех вкладок; вкладка и «скрыть
   *  повторы» не действуют. */
  vorOnly?: boolean;
  /** Признаки ревизии для вкладок ([`tabContext`]); нет — вкладки по части
   *  РД. */
  ctx?: TabContext;
}

/** Строка ВОР из списка «Есть в ВОР, нет в СП» (признак бэкенда, выпуск 2). */
export function isVorNotInSpec(id: string, marks: ReadonlyMap<string, PtoVolumeMark>): boolean {
  return marks.get(id)?.vor_not_in_spec === true;
}

/** Строки вкладки с фильтрами; порядок — как пришёл (порядок РД). Признаков
 *  отбора ещё нет — повторов не знаем, ничего не прячем. */
export function filterVolumes<T extends FilterFields>(
  items: readonly T[],
  marks: ReadonlyMap<string, PtoVolumeMark>,
  f: VolumeFilter,
): T[] {
  if (f.vorOnly) {
    return items.filter((p) => isVorNotInSpec(p.id, marks) && (!f.onlyReview || p.needs_review));
  }
  return items.filter(
    (p) =>
      volumeTab(p, marks.get(p.id), f.ctx) === f.tab &&
      (!f.hideRepeats || !isRepeat(p.id, marks)) &&
      (!f.onlyReview || p.needs_review),
  );
}

/** Сколько строк на вкладке: всего, показано, спрятано как повторы, на проверке
 *  среди показанных (фильтр «Только на проверку» не учитывается). */
export interface TabStat {
  total: number;
  shown: number;
  hidden: number;
  review: number;
}

export function tabStats(
  items: readonly FilterFields[],
  marks: ReadonlyMap<string, PtoVolumeMark>,
  hideRepeats: boolean,
  ctx?: TabContext,
): Record<VolumeTab, TabStat> {
  const out: Record<VolumeTab, TabStat> = {
    materials: { total: 0, shown: 0, hidden: 0, review: 0 },
    works: { total: 0, shown: 0, hidden: 0, review: 0 },
    other: { total: 0, shown: 0, hidden: 0, review: 0 },
  };
  for (const p of items) {
    const s = out[volumeTab(p, marks.get(p.id), ctx)];
    s.total += 1;
    if (hideRepeats && isRepeat(p.id, marks)) {
      s.hidden += 1;
      continue;
    }
    s.shown += 1;
    if (p.needs_review) s.review += 1;
  }
  return out;
}

/** Вкладка при открытии экрана: материалы, если там есть строки, затем
 *  «Прочее» (листы чертежей), затем работы. */
export function defaultTab(stats: Record<VolumeTab, TabStat>): VolumeTab {
  for (const t of ['materials', 'other', 'works'] as const) {
    if (stats[t].total > 0) return t;
  }
  return 'materials';
}

function isTableRow(p: Pick<PtoVolumePosition, 'source_role'>): boolean {
  return p.source_role === 'rd_table' || p.source_role === 'rd_geometry';
}

/** Связь строки с оригиналом в разметке R2: повтор, вероятный или возможный
 *  повтор строки сводной. */
function hasLink(raw: unknown): boolean {
  const r = asObj(raw);
  return !!(asObj(r?.dup_of) || asObj(r?.dup_candidate) || asObj(r?.dup_possible));
}

/** Счётчики над вкладками: «в ведомости N строк (P позиций) · повторы на
 *  чертежах M · без пары K · есть в ВОР, нет в СП L». При правилах выпуска 2
 *  («СП01 основной») — по признакам бэкенда: перечень материалов, лист
 *  «Сверка», «нет в СП01» и список «Есть в ВОР, нет в СП» — те же строки, что в
 *  Excel. */
export interface VolumeCounters {
  /** Позиций в сумме ведомости материалов (число вошло в строку, которая идёт
   *  в итоги). Без правил выпуска 2 — позиций в сумме на вкладках «Материалы»
   *  и «Прочее» (строки ВОР — на своей вкладке). */
  inVedomost: number;
  /** Строк листа «Ведомость материалов» Excel — `counts.vedomost_rows` (выпуск
   *  2): строка собирает одинаковые позиции разных листов. null — правила
   *  выпуска 2 выключены или сервер числа не прислал: счётчик выпуска 1б. */
  vedomostRows: number | null;
  /** Строки таблиц на чертежах вне суммы как повтор: их число учтено в другой
   *  строке (обычно — в строке СП01); при правилах выпуска 2 — строки листа
   *  «Сверка (ЧТЖ и ВР)» Excel. */
  drawingRepeats: number;
  /** Строки спецификаций на чертежах в сумме без пары в СП01 («нет в СП01 —
   *  проверьте»); null — в ревизии нет строк СП01 и сводной таблицы (у АР2 она
   *  на ЧТЖ02), сверять не с чем. */
  unpaired: number | null;
  /** «Есть в ВОР, нет в СП» — число бэкенда (выпуск 2); null — правила
   *  выпуска 2 выключены (или сервер их не знает): счётчика нет. */
  vorNotInSp: number | null;
}

/** Поля позиции для счётчиков. */
export type CounterFields = TabFields & Pick<PtoVolumePosition, 'id'>;

/** Режим признаков для счётчиков. */
export interface CounterOptions {
  /** Признаки по правилам выпуска 2 («СП01 основной», `spec_only` ответа). */
  specOnly?: boolean;
  /** «Есть в ВОР, нет в СП» — `counts.vor_not_in_spec` ответа. */
  vorNotInSpec?: number | null;
  /** Строк ведомости материалов, как в Excel, — `counts.vedomost_rows` ответа. */
  vedomostRows?: number | null;
}

/** Склонение по числу: «1 строка», «2 строки», «5 строк», «11 строк». */
export function plural(n: number, [one, few, many]: readonly [string, string, string]): string {
  const m10 = Math.abs(n) % 10;
  const m100 = Math.abs(n) % 100;
  if (m100 >= 11 && m100 <= 14) return many;
  if (m10 === 1) return one;
  if (m10 >= 2 && m10 <= 4) return few;
  return many;
}

/** Число с разрядами, как в остальных подписях экрана: «1 332». */
export function countText(n: number): string {
  return n.toLocaleString('ru-RU');
}

const ROW_FORMS = ['строка', 'строки', 'строк'] as const;
const POSITION_FORMS = ['позиция', 'позиции', 'позиций'] as const;

/** Слова счётчика «в ведомости 584 строки (1 332 позиции)» — после числа
 *  строк ведомости и числа позиций экрана в сумме. */
export function vedomostWords(rows: number, positions: number): { rows: string; positions: string } {
  return { rows: plural(rows, ROW_FORMS), positions: plural(positions, POSITION_FORMS) };
}

/** Счётчики по признакам отбора; признаков нет (не загружены) — null. */
export function volumeCounters(
  items: readonly CounterFields[],
  marks: ReadonlyMap<string, PtoVolumeMark>,
  opts: CounterOptions = {},
): VolumeCounters | null {
  if (marks.size === 0) return null;
  const specOnly = opts.specOnly === true;
  let inVedomost = 0;
  let drawingRepeats = 0;
  let unpaired = 0;
  let hasSummary = false;
  for (const p of items) {
    // Счётчики ведомости — по части РД (выпуск 1б): «повторы на чертежах» и
    // «без пары» — строки листов чертежей против СП01 или сводной.
    const tab = partTab(p);
    const m = marks.get(p.id);
    if (isTableRow(p) && (tab === 'materials' || asObj(p.raw)?.summary_table === true)) {
      hasSummary = true;
    }
    if (specOnly) {
      // Признаки бэкенда — те же строки, что в Excel: перечень, «Сверка»,
      // «нет в СП01».
      if (m?.in_sum) inVedomost += 1;
      if (m?.status === 'repeat') drawingRepeats += 1;
      if (m?.in_sum && m.unpaired) unpaired += 1;
      continue;
    }
    if (m?.in_sum && tab !== 'works') inVedomost += 1;
    if (tab !== 'other' || !isTableRow(p)) continue;
    if (m?.repeat) drawingRepeats += 1;
    const raw = asObj(p.raw);
    if (m?.in_sum && raw?.table_kind === 'spec' && raw.summary_table !== true && !hasLink(raw)) {
      unpaired += 1;
    }
  }
  return {
    inVedomost,
    vedomostRows:
      specOnly && typeof opts.vedomostRows === 'number' ? opts.vedomostRows : null,
    drawingRepeats,
    unpaired: hasSummary ? unpaired : null,
    vorNotInSp: specOnly ? (opts.vorNotInSpec ?? 0) : null,
  };
}

/** Строка, которую надо показать по ссылке: её вкладка и нужно ли снять
 *  «скрыть повторы» (список «Есть в ВОР, нет в СП» при этом закрывается). */
export function revealFor(
  p: FilterFields,
  marks: ReadonlyMap<string, PtoVolumeMark>,
  ctx?: TabContext,
): { tab: VolumeTab; showRepeats: boolean } {
  return { tab: volumeTab(p, marks.get(p.id), ctx), showRepeats: isRepeat(p.id, marks) };
}
